import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearAuth, getAccessToken, setAccessToken } from './auth';

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // refresh-token httpOnly cookie
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ---- Request interceptor: attach Authorization header ----
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ---- 401 refresh queue ----
// Prevents thundering herd: if a refresh is already in flight, other failed
// requests wait for the same promise instead of firing their own /auth/refresh.

let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  // Use a bare axios instance so we don't recurse through the interceptor.
  const res = await axios.post<{ data: { accessToken: string } }>(
    `${BASE_URL}/auth/refresh`,
    {},
    { withCredentials: true },
  );
  // Backend wraps every JSON response in `{ data: ... }`.
  const token = res.data?.data?.accessToken;
  if (!token) throw new Error('Refresh did not return an access token');
  setAccessToken(token);
  return token;
}

// ---- Response interceptor: unwrap backend envelope ----
// The backend returns every success payload as `{ data: T, meta? }`. Hooks are
// typed against the inner shape (e.g. `api.get<KeyItem>(...)` → `res.data`
// should be `KeyItem`), so here we flatten the envelope:
//   { data: X }                 → X
//   { data: X, meta: { ... } }  → { data: X, ...meta }   (paginated lists)
// Non-JSON bodies (blobs for CSV export) are passed through untouched.
api.interceptors.response.use((res) => {
  const body = res.data as unknown;
  if (body && typeof body === 'object' && !Array.isArray(body) && !(body instanceof Blob)) {
    const env = body as { data?: unknown; meta?: Record<string, unknown> };
    if ('data' in env) {
      if (env.meta && typeof env.meta === 'object') {
        res.data = { data: env.data, ...env.meta };
      } else {
        res.data = env.data;
      }
    }
  }
  return res;
});

interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// ---- Response interceptor: attempt one refresh, retry once ----
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;
    const status = error.response?.status;

    // Don't try to refresh the refresh endpoint itself, or the login endpoint.
    const url = originalRequest?.url ?? '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout');

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        if (!refreshInFlight) {
          refreshInFlight = performRefresh().finally(() => {
            refreshInFlight = null;
          });
        }
        const newToken = await refreshInFlight;
        originalRequest.headers = originalRequest.headers ?? {};
        (originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        return api.request(originalRequest);
      } catch (refreshErr) {
        clearAuth();
        // Let the app-level effect redirect to /login — don't hard-navigate here.
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  },
);

// ---- Convenience helpers ----

export function apiError(err: unknown): { code: string; message: string } {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    if (data?.error?.message) {
      return {
        code: data.error.code ?? 'UNKNOWN',
        message: data.error.message,
      };
    }
    return {
      code: String(err.response?.status ?? 'NETWORK'),
      message: err.message || 'Network error',
    };
  }
  if (err instanceof Error) return { code: 'UNKNOWN', message: err.message };
  return { code: 'UNKNOWN', message: 'Unknown error' };
}

/** Download a binary response as a file — used for CSV export. */
export async function downloadBlob(path: string, filename: string): Promise<void> {
  const res = await api.get(path, { responseType: 'blob' });
  const blob = res.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
